// ═══════════════════════════════════════════════════════════════
// V·IANet Governance — ProcessMapLevel1 (Orbital Redesign)
// Portal de inicio: AppHeader + KPIs + Orbital PE/PO/PS + News
// ═══════════════════════════════════════════════════════════════

import { useState } from 'react';
import { PM } from '../../../../../config/processMapStyles';
import { useI18n } from '../../../../../i18n';
import { LoadingSpinner } from '../../../../shared/components/LoadingSpinner';
import { useProcessGroups } from '../hooks/useProcessGroups';
import { CommunicationCards } from './CommunicationCards';
import { NewsEditorialPanel } from './NewsEditorialPanel';
import { ProcessMapOrbital } from './ProcessMapOrbital';
import { IconTrendUp, IconTrendDown, IconTrendStable } from './ProcessMapIcons';
import { InfoTooltip } from '@/components/ui/InfoTooltip';

// ─── KPI data (placeholder) ─────────────────────────────────

interface KpiCard {
  titulo: string;
  valor: string;
  tendencia: 'up' | 'down' | 'stable';
  descripcion: string;
}

const TrendIcon = ({ t, size = 16 }: { t: 'up' | 'down' | 'stable'; size?: number }) => {
  const color = t === 'up' ? PM.verdeOk : t === 'down' ? PM.rojoError : PM.ambar;
  if (t === 'up') return <IconTrendUp size={size} color={color} />;
  if (t === 'down') return <IconTrendDown size={size} color={color} />;
  return <IconTrendStable size={size} color={color} />;
};

// ═══════════════════════════════════════════════════════════════
// Component
// ═══════════════════════════════════════════════════════════════

export function ProcessMapLevel1(_props: { embedded?: boolean }) {
  const { t } = useI18n();
  const { tipologias, loading, error } = useProcessGroups();

  if (loading) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <LoadingSpinner text={t.processMap.cargandoMapa} />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div
            style={{
              color: PM.rojoError,
              fontSize: 13,
              backgroundColor: '#FEF2F2',
              padding: '12px 20px',
              borderRadius: 10,
              border: '1px solid #FECACA',
            }}
          >
            {t.processMap.errorAlCargar}: {error}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
      <div style={{ flex: 1, display: 'flex', overflow: 'hidden' }}>
        {/* Left column: KPIs + Orbital + Corp News */}
        <div
          style={{
            flex: 1,
            padding: '20px 24px',
            display: 'flex',
            flexDirection: 'column',
            gap: 16,
            height: '100%',
            overflow: 'hidden',
          }}
        >
          {/* Header con tooltip */}
          <div style={{ flexShrink: 0 }}>
            <h1
              style={{
                fontSize: 18,
                fontWeight: 700,
                color: '#1F3864',
                margin: 0,
                display: 'inline-flex',
                alignItems: 'center',
                fontFamily: 'DM Sans, sans-serif',
              }}
            >
              {t.process.mapaTitle}
              <InfoTooltip
                title={t.process.tooltips.mapa.titulo}
                text={t.process.tooltips.mapa.ayuda}
              />
            </h1>
          </div>

          {/* KPI Strip */}
          <div style={{ flexShrink: 0 }}>
            <KpiStrip />
          </div>

          {/* Orbital Process Map — fills remaining space */}
          <div style={{ flex: 1, minHeight: 0 }}>
            <ProcessMapOrbital tipologias={tipologias} />
          </div>

          {/* Corporate Communication card */}
          <div style={{ flexShrink: 0 }}>
            <CommunicationCards />
          </div>
        </div>

        {/* News panel in card (aligned top with KPIs, bottom with corp news) */}
        <div
          style={{
            width: 340,
            minWidth: 340,
            flexShrink: 0,
            margin: '20px 24px 20px 0',
            backgroundColor: PM.surface,
            borderRadius: PM.card.borderRadius,
            border: PM.card.border,
            boxShadow: PM.card.shadow,
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <NewsEditorialPanel />
        </div>
      </div>
    </div>
  );
}

// ─── KPI Strip (7 cards, wrapping) ──────────────────────────

function KpiStrip() {
  const { t } = useI18n();
  const [hoveredIdx, setHoveredIdx] = useState<number | null>(null);

  const kpiData: KpiCard[] = [
    {
      titulo: t.processMap.kpi.procesosDocumentados,
      valor: '47',
      tendencia: 'up',
      descripcion: t.processMap.kpi.esteTrimestre(5),
    },
    {
      titulo: t.processMap.kpi.flujogramasPublicados,
      valor: '32',
      tendencia: 'up',
      descripcion: t.processMap.kpi.delTotal(68),
    },
    {
      titulo: t.processMap.kpi.documentosVigentes,
      valor: '124',
      tendencia: 'stable',
      descripcion: t.processMap.kpi.sinCambios,
    },
    {
      titulo: t.processMap.kpi.auditoriasPendientes,
      valor: '3',
      tendencia: 'down',
      descripcion: t.processMap.kpi.vsMesAnterior(2),
    },
    {
      titulo: t.processMap.kpi.conformidad,
      valor: '94%',
      tendencia: 'up',
      descripcion: t.processMap.kpi.vsUltimoCiclo(2),
    },
    {
      titulo: t.processMap.kpi.procesosEnRevision,
      valor: '8',
      tendencia: 'stable',
      descripcion: t.processMap.kpi.pendientesAprobacion,
    },
    {
      titulo: t.processMap.kpi.indicadoresCumplidos,
      valor: '87%',
      tendencia: 'up',
      descripcion: t.processMap.kpi.vsObjetivoAnual,
    },
  ];

  return (
    <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap' }}>
      {kpiData.map((kpi, idx) => (
        <div
          key={idx}
          onMouseEnter={() => setHoveredIdx(idx)}
          onMouseLeave={() => setHoveredIdx(null)}
          style={{
            flex: '1 1 160px',
            minWidth: 140,
            backgroundColor: PM.surface,
            borderRadius: PM.card.borderRadius,
            border: PM.card.border,
            boxShadow: hoveredIdx === idx ? PM.card.shadowHover : PM.card.shadow,
            transform: hoveredIdx === idx ? 'translateY(-2px)' : 'none',
            transition: PM.card.transition,
            padding: '16px 18px',
            display: 'flex',
            flexDirection: 'column',
            gap: 6,
          }}
        >
          <div style={{ fontSize: 11, color: PM.textSec, fontWeight: 500 }}>{kpi.titulo}</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 28, fontWeight: 800, color: PM.text, lineHeight: 1 }}>
              {kpi.valor}
            </span>
            <TrendIcon t={kpi.tendencia} />
          </div>
          <div style={{ fontSize: 10, color: PM.textMut }}>{kpi.descripcion}</div>
        </div>
      ))}
    </div>
  );
}
